import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { AuthShell } from '@/components/portal/AuthShell'
import { PortalField } from '@/components/portal/PortalField'
import { PortalInput } from '@/components/portal/PortalInput'
import { PasswordInput } from '@/components/portal/PasswordInput'
import { Checkbox } from '@/components/ui/checkbox'
import { authErrorI18nKey, loginWithCognito, requestPasswordReset } from '@/lib/auth'
import { ensureClientProfile } from '@/lib/client-id'
import { safeNextPath } from '@/lib/safe-next-path'
import { useAuthStore } from '@/store/auth'

const signInSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  rememberMe: z.boolean().optional(),
})

type SignInForm = z.infer<typeof signInSchema>

export function SignInPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const location = useLocation()
  const verifiedNotice = (location.state as { notice?: string } | null)?.notice === 'verifiedSignIn'
  const [resetSent, setResetSent] = useState(false)
  const [password, setPassword] = useState('')

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
    defaultValues: { rememberMe: false },
  })

  async function onSubmit(data: SignInForm) {
    setError(null)
    try {
      await loginWithCognito(data)
      try {
        await ensureClientProfile(useAuthStore.getState().user)
      } catch {
        // optional on first login
      }
      navigate(safeNextPath(new URLSearchParams(location.search).get('next')) ?? '/dashboard')
    } catch (err) {
      const key = authErrorI18nKey(err)
      setError(key ? t(key) : t('common.error'))
    }
  }

  async function handleForgotPassword() {
    const email = getValues('email')
    if (!email) {
      setError(t('validation.invalidEmail'))
      return
    }
    try {
      await requestPasswordReset(email)
      setResetSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'))
    }
  }

  return (
    <AuthShell>
      <h1 className="prod-auth__title">{t('auth.signIn')}</h1>
      <p className="prod-auth__subtitle">{t('auth.signInSubtitle')}</p>
      {verifiedNotice ? <p className="prod-success">{t('auth.verifiedSignInNext')}</p> : null}
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
        <PortalField label={t('auth.email')} required error={errors.email ? t('validation.invalidEmail') : undefined}>
          <PortalInput
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            autoComplete="username"
            {...register('email')}
          />
        </PortalField>
        <PortalField label={t('auth.password')} required error={errors.password ? t('validation.required') : undefined}>
          <PasswordInput
            value={password}
            autoComplete="current-password"
            onChange={(v) => {
              setPassword(v)
              setValue('password', v, { shouldValidate: true })
            }}
            placeholder={t('auth.passwordPlaceholder')}
          />
        </PortalField>
        <div className="prod-form-row">
          <Checkbox label={t('auth.rememberMe')} {...register('rememberMe')} />
          <button type="button" className="prod-link prod-link-btn" onClick={() => void handleForgotPassword()}>
            {t('auth.forgotPassword')}
          </button>
        </div>
        {resetSent ? <p className="prod-success">{t('auth.resetPasswordHint')}</p> : null}
        {error ? <p className="prod-error">{error}</p> : null}
        <button type="submit" className="prod-btn prod-btn--gold prod-btn--gold-full" disabled={isSubmitting}>
          {t('auth.signIn')}
        </button>
        <p className="prod-auth__subtitle prod-auth__subtitle--after">
          {t('auth.noAccount')}{' '}
          <Link to="/sign-up" className="prod-link">
            {t('auth.clickToSignUp')}
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
